import React from 'react';
import { Link } from 'react-router-dom';
import GradeBadge from './GradeBadge';
import VideoThumbnail from '../../components/VideoThumbnail';
import { useSiteData } from '../data/SiteDataContext';
import { resolveMediaUrl } from '../../utils/media';
import { VIDEOS_PATH, getPostCustomThumbnail, getPostVideo } from '../../utils/video';
import {
    formatByline,
    formatCardDate,
    formatReadTime,
    getPostDateTimeAttr,
} from '../lib/format';
// Play badge styles (shared with the player).
import '../../components/VideoEmbed.css';

// `label` is an optional eyebrow above the title (e.g. "Part 2" on series pages).
function ArticleCard({ post, label }) {
    const { getPostCategory } = useSiteData();
    const category = getPostCategory(post);
    const permalink = `/posts/${post._id}`;

    // Condoned Media cards show the video's own frame (or the writer's
    // upload, if any) under a play badge, file under Condoned Media and say
    // "Video" where articles give their read time.
    const video = getPostVideo(post);
    const thumbnailSrc = video ? '' : resolveMediaUrl(post.thumbnail);

    return (
        <article className={`article-card${video ? ' article-card--video' : ''}`} role="listitem">
            <Link
                className="article-card-link"
                to={permalink}
                aria-label={video ? `${post.title} (video)` : post.title}
            />
            {(video || thumbnailSrc) && (
                <Link
                    className={`article-card-thumb grain-wrap${video ? ' article-card-thumb--video' : ''}`}
                    to={permalink}
                    tabIndex={-1}
                    aria-hidden="true"
                >
                    {video ? (
                        <VideoThumbnail video={video} src={getPostCustomThumbnail(post)} alt="" loading="lazy" />
                    ) : (
                        <img src={thumbnailSrc} alt="" loading="lazy" />
                    )}
                    {video && <span className="ds-play-badge">▶</span>}
                </Link>
            )}
            <div className="article-card-body">
                {video ? (
                    <div className="article-card-category-row">
                        <span className="article-card-category">
                            <Link to={VIDEOS_PATH}>Condoned Media</Link>
                        </span>
                    </div>
                ) : category && (
                    <div className="article-card-category-row">
                        <span className="article-card-category">
                            <Link to={`/category/${category.id}`}>{category.label}</Link>
                        </span>
                    </div>
                )}
                <div className="article-card-grade-row">
                    <GradeBadge rating={post.confidenceScoreRating} />
                </div>
                {label && <div className="article-card-label">{label}</div>}
                <h2 className="article-card-title">
                    <Link to={permalink}>{post.title}</Link>
                </h2>
                <p className="article-card-excerpt">{post.description}</p>
                <footer className="article-card-footer">
                    <span className="article-byline">{formatByline(post)}</span>
                    <span className="sep">·</span>
                    <time className="article-date" dateTime={getPostDateTimeAttr(post)}>
                        {formatCardDate(post)}
                    </time>
                    <span className="sep">·</span>
                    {video ? (
                        <span className="article-read-time article-card-kind">▶ Video</span>
                    ) : (
                        <span className="article-read-time">{formatReadTime(post)}</span>
                    )}
                </footer>
            </div>
        </article>
    );
}

export default ArticleCard;
